import { MouseEvent, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import {
	Box,
	Button,
	Divider,
	FormControlLabel,
	Grid,
	Popover,
	Radio,
	RadioGroup,
	Stack,
	Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "dayjs/locale/pt-br";

import RangeCalendar from "../../../shared/components/range-calendar";
import {
	PERIOD_PRESETS,
	findMatchingPreset,
	formatPeriodParam,
	getPeriodRange,
} from "./periodPresets";

const MAX_CUSTOM_RANGE_DAYS = 366;

export function OrdersPeriodFilter() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const [from, to] = getPeriodRange(searchParams);
	const activePreset = findMatchingPreset(from, to);

	const [selectedPresetKey, setSelectedPresetKey] = useState<string | null>(
		null,
	);
	const [customFrom, setCustomFrom] = useState<Dayjs | null>(from);
	const [customTo, setCustomTo] = useState<Dayjs | null>(to);

	const label = activePreset
		? activePreset.label
		: `${from.format("DD/MM/YYYY")} - ${to.format("DD/MM/YYYY")}`;

	const customRangeTooLong = Boolean(
		customFrom &&
			customTo &&
			customTo.diff(customFrom, "day") > MAX_CUSTOM_RANGE_DAYS,
	);

	function openMenu(event: MouseEvent<HTMLElement>) {
		setSelectedPresetKey(activePreset?.key ?? null);
		setCustomFrom(from);
		setCustomTo(to);
		setAnchorEl(event.currentTarget);
	}

	function closeMenu() {
		setAnchorEl(null);
	}

	function handlePresetSelect(key: string) {
		setSelectedPresetKey(key);
		const preset = PERIOD_PRESETS.find((item) => item.key === key);
		if (preset) {
			const [start, end] = preset.getRange();
			setCustomFrom(start);
			setCustomTo(end);
		}
	}

	function handleCustomFromChange(value: Dayjs | null) {
		setSelectedPresetKey(null);
		setCustomFrom(value);
	}

	function handleCustomToChange(value: Dayjs | null) {
		setSelectedPresetKey(null);
		setCustomTo(value);
	}

	function handleCalendarChange(range: DateRange | undefined) {
		setSelectedPresetKey(null);
		setCustomFrom(range?.from ? dayjs(range.from) : null);
		setCustomTo(range?.to ? dayjs(range.to) : null);
	}

	function handleApply() {
		if (!customFrom || !customTo || customRangeTooLong) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("from", formatPeriodParam(customFrom));
				next.set("to", formatPeriodParam(customTo));
				next.delete("page");
				return next;
			},
			{ replace: true },
		);
		closeMenu();
	}

	function handleClear() {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("from");
				next.delete("to");
				next.delete("page");
				return next;
			},
			{ replace: true },
		);
		closeMenu();
	}

	return (
		<>
			<Button
				onClick={openMenu}
				startIcon={<CalendarMonthIcon fontSize="small" />}
				endIcon={<ArrowDropDownIcon />}
				variant="outlined"
				color="inherit"
				size="large"
				sx={{
					textTransform: "none",
					borderColor: "divider",
					color: "text.primary",
				}}
			>
				{label}
			</Button>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={closeMenu}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			>
				<Box sx={{ p: 2, width: { xs: 320, sm: 700 }, maxWidth: "95vw" }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "column", md: "row" },
							gap: 2,
						}}
					>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<RangeCalendar
								from={customFrom?.toDate()}
								to={customTo?.toDate()}
								onChange={handleCalendarChange}
							/>
						</Box>

						<Box sx={{ flex: 1, minWidth: { xs: 0, md: 300 } }}>
							<RadioGroup
								value={selectedPresetKey ?? ""}
								onChange={(event) => handlePresetSelect(event.target.value)}
							>
								{PERIOD_PRESETS.map((preset) => (
									<FormControlLabel
										key={preset.key}
										value={preset.key}
										control={<Radio size="small" />}
										label={preset.label}
									/>
								))}
							</RadioGroup>

							<Divider sx={{ my: 1.5 }} />

							<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
								Período personalizado
							</Typography>
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale="pt-br"
							>
								<Grid
									container
									spacing={1}
									sx={{ mb: customRangeTooLong ? 0.5 : 2 }}
								>
									<Grid item xs={6}>
										<DatePicker
											label="De"
											value={customFrom}
											onChange={handleCustomFromChange}
											format="DD/MM/YYYY"
											slotProps={{
												textField: { size: "small", fullWidth: true },
											}}
										/>
									</Grid>
									<Grid item xs={6}>
										<DatePicker
											label="Até"
											value={customTo}
											onChange={handleCustomToChange}
											format="DD/MM/YYYY"
											slotProps={{
												textField: { size: "small", fullWidth: true },
											}}
										/>
									</Grid>
								</Grid>
							</LocalizationProvider>

							{customRangeTooLong && (
								<Typography
									variant="caption"
									color="error"
									sx={{ display: "block", mb: 1.5 }}
								>
									O período personalizado pode abranger no máximo 1 ano.
								</Typography>
							)}
						</Box>
					</Box>

					<Stack
						direction="row"
						spacing={1}
						justifyContent="flex-end"
						sx={{ mt: 2 }}
					>
						<Button onClick={handleClear}>Limpar</Button>
						<Button
							variant="contained"
							disabled={!customFrom || !customTo || customRangeTooLong}
							onClick={handleApply}
						>
							Aplicar
						</Button>
					</Stack>
				</Box>
			</Popover>
		</>
	);
}
